/**
 * Centralna konfiguracja systemu subskrypcji
 *
 * Ten plik jest JEDYNYM miejscem gdzie definiujemy:
 * - Typy subskrypcji
 * - Mapowanie menu dla każdej roli (UNIFIED_MENU)
 * - Ochronę tras
 *
 * Aby dodać nową funkcjonalność zależną od subskrypcji:
 * 1. Dodaj odpowiedni wpis w UNIFIED_MENU z odpowiednimi flagami:
 *    - roles: ['admin', 'adminves', 'user', 'userves', 'superuser']
 *    - allowCoordinator: true/false
 *    - allowInspector: true/false
 *    - requiredSubscriptions: [SUBSCRIPTION_TYPES.XXX]
 * 2. Dodaj ochronę trasy w ROUTE_PROTECTION
 *
 * UWAGA: ADMIN_MENU i USER_MENU są przestarzałe (deprecated).
 * Używaj UNIFIED_MENU zamiast nich.
 */

// ============================================================================
// TYPY SUBSKRYPCJI
// ============================================================================

export const SUBSCRIPTION_TYPES = {
  NOMINATIONS: 1,
  WORK_PERMITS: 2,
  TRAININGS: 3,
  PPE: 4,
  WAREHOUSE: 5,
  TIMESHEETS: 6,
  HSE: 7,
  PLANNING_BOARD: 8,
  CARS_EQUIPMENT: 9,
  CONSTRUCTION_PROJECT_ORDER: 10,
  PPE_VIA_COOPERATION: 11,
};

// Mapowanie ID subskrypcji na klucze z API
export const SUBSCRIPTION_KEYS = {
  [SUBSCRIPTION_TYPES.NOMINATIONS]: 'nominations',
  [SUBSCRIPTION_TYPES.WORK_PERMITS]: 'work_permits',
  [SUBSCRIPTION_TYPES.TRAININGS]: 'trainings',
  [SUBSCRIPTION_TYPES.PPE]: 'ppe',
  [SUBSCRIPTION_TYPES.WAREHOUSE]: 'warehouse',
  [SUBSCRIPTION_TYPES.TIMESHEETS]: 'timesheets',
  [SUBSCRIPTION_TYPES.HSE]: 'hse',
  [SUBSCRIPTION_TYPES.PLANNING_BOARD]: 'planning_board',
  [SUBSCRIPTION_TYPES.CARS_EQUIPMENT]: 'cars_equipment',
  [SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER]: 'construction_project_order',
  [SUBSCRIPTION_TYPES.PPE_VIA_COOPERATION]: 'ppe_via_cooperation',
};

// Nazwy subskrypcji do wyświetlania
export const SUBSCRIPTION_NAMES = {
  [SUBSCRIPTION_TYPES.NOMINATIONS]: 'Nominations',
  [SUBSCRIPTION_TYPES.WORK_PERMITS]: 'Work Permits',
  [SUBSCRIPTION_TYPES.TRAININGS]: 'Trainings',
  [SUBSCRIPTION_TYPES.PPE]: 'PPE',
  [SUBSCRIPTION_TYPES.WAREHOUSE]: 'Warehouse',
  [SUBSCRIPTION_TYPES.TIMESHEETS]: 'Timesheets',
  [SUBSCRIPTION_TYPES.HSE]: 'HSE',
  [SUBSCRIPTION_TYPES.PLANNING_BOARD]: 'Planning Board',
  [SUBSCRIPTION_TYPES.CARS_EQUIPMENT]: 'Cars & Equipment',
  [SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER]: 'Construction Project Order',
};

// ============================================================================
// STARE MENU (ADMIN_MENU, USER_MENU) - USUNIĘTE
// Zastąpione przez UNIFIED_MENU poniżej
// ============================================================================

// DEPRECATED - używaj UNIFIED_MENU zamiast ADMIN_MENU
// Ten export jest zachowany dla kompatybilności wstecznej (AppLayout.js)
export const ADMIN_MENU = [
  // ========================================
  // 1. Dashboard - zawsze widoczny dla admina
  // ========================================
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/admin',
    requiredSubscriptions: [], // Zawsze widoczny - treść będzie się zmieniać w zależności od subskrypcji
    roles: ['admin', 'adminves'],
  },

  // ========================================
  // 2. USERS - widoczny gdy JAKAKOLWIEK subskrypcja wymaga użytkowników
  // ========================================
  {
    id: 'users',
    label: 'Users',
    icon: 'Users',
    path: '/users',
    requiredSubscriptions: [
      SUBSCRIPTION_TYPES.NOMINATIONS,
      SUBSCRIPTION_TYPES.WORK_PERMITS,
      SUBSCRIPTION_TYPES.TRAININGS,
      SUBSCRIPTION_TYPES.PPE,
      SUBSCRIPTION_TYPES.WAREHOUSE,
      SUBSCRIPTION_TYPES.TIMESHEETS,
      SUBSCRIPTION_TYPES.PLANNING_BOARD,
    ],
    roles: ['admin', 'adminves'],
    submenu: [
      { id: 'users-manage', label: 'Manage Users', path: '/users' },
      { id: 'users-roles', label: 'Roles', path: '/users/roles' },
      { id: 'users-archives', label: 'Archives', path: '/users/archives' },
      { id: 'users-contracts', label: 'Contracts & Rates', path: '/users/contracts' },
    ],
  },

  // ========================================
  // 3. CERTIFICATES - subskrypcja TRAININGS (ID: 3)
  // ========================================
  {
    id: 'certificateexp',
    label: 'Certificates',
    icon: 'AlarmClock',
    path: '/certificateexp',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TRAININGS],
    roles: ['admin', 'adminves'],
    submenu: [
      { id: 'certificates-overview', label: 'Overview', path: '/certificateexp' },
      { id: 'documents-to-verify', label: 'Documents to Verify', path: '/documents-to-verify' },
    ],
  },

  // ========================================
  // 4. TRAININGS - subskrypcja ID: 3
  // ========================================
  {
    id: 'trainings',
    label: 'Trainings',
    icon: 'GraduationCap',
    path: '/trainings',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TRAININGS],
    roles: ['admin', 'adminves'],
    submenu: [
      { id: 'trainings-users', label: 'User Panel', path: '/trainings/users' },
      { id: 'trainings-panel', label: 'Training Panel', path: '/trainings/panel' },
      { id: 'trainings-management', label: 'Management', path: '/trainings/management' },
      { id: 'trainings-analysis', label: 'Cost Analysis', path: '/trainings/analysis' },
      { id: 'trainings-emails', label: 'Email Templates', path: '/trainings/emails' },
    ],
  },

  // ========================================
  // 5. NOMINATIONS - subskrypcja ID: 1
  // ========================================
  {
    id: 'nominations',
    label: 'Nominations',
    icon: 'Award',
    path: '/nominations/list',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.NOMINATIONS],
    roles: ['admin', 'adminves'],
    submenu: [
      { id: 'nominations-create', label: 'Create', path: '/nominations/create' },
      { id: 'nominations-list', label: 'Nominations List', path: '/nominations/list' },
    ],
  },

  // ========================================
  // 6. WORK PERMITS - subskrypcja ID: 2
  // ========================================
  {
    id: 'workpermits',
    label: 'Work Permits',
    icon: 'Send',
    path: '/workpermits',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WORK_PERMITS],
    roles: ['admin'],
    submenu: [
      { id: 'workpermits-list', label: 'All Permits', path: '/workpermits' },
      { id: 'workpermits-teams', label: 'Teams', path: '/teammanager' },
    ],
  },
  {
    id: 'workpermit-vestas',
    label: 'Work Orders',
    icon: 'Send',
    path: '/workpermit/admin',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WORK_PERMITS],
    roles: ['adminves'],
  },

  // ========================================
  // 7. PPE (Equipment) - subskrypcja ID: 4
  // ========================================
  {
    id: 'equipment',
    label: 'Equipment',
    icon: 'HardHat',
    path: '/equipment',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.PPE],
    roles: ['admin'],
    allowInspector: true, // Inspektorzy (nawet zwykli userzy) mogą widzieć Equipment
    allowCoordinator: true, // Coordinatorzy widzą sprzęt całej firmy (jak admin)
    submenu: [
      { id: 'equipment-overview', label: 'Overview', path: '/equipment' },
      { id: 'equipment-management', label: 'Equipment Management', path: '/equipment/management' },
      { id: 'equipment-logbook', label: 'Equipment Log Book', path: '/equipment/logbook' },
      { id: 'equipment-defect-reports', label: 'Defect Reports', path: '/equipment/defect-reports' },
    ],
  },

  // =====================================================
  // 7b. Equipment via cooperation (no own PPE sub)
  // Widoczne tylko gdy firma ma ppe_via_cooperation
  // i NIE ma własnej subskrypcji PPE
  // =====================================================
  {
    id: 'equipment-cooperation',
    label: 'Equipment',
    icon: 'HardHat',
    path: '/equipment',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.PPE_VIA_COOPERATION],
    hideIfHasPpe: true,
    permissionId: 'equipment',
    roles: ['admin'],
    submenu: [
      { id: 'equipment-management', label: 'Equipment Management', path: '/equipment/management' },
    ],
  },

  // ========================================
  // 8. WAREHOUSE - subskrypcja ID: 5
  // ========================================
  {
    id: 'warehouse',
    label: 'Warehouse',
    icon: 'Package',
    path: '/warehouse/inventory',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WAREHOUSE],
    roles: ['admin'],
    submenu: [
      { id: 'warehouse-inventory', label: 'Inventory', path: '/warehouse/inventory' },
      { id: 'warehouse-history', label: 'Allocations', path: '/warehouse/history' },
      { id: 'warehouse-settings', label: 'Settings', path: '/warehouse/settings' },
      { id: 'warehouse-project-orders', label: 'Project Orders', path: '/warehouse/project-orders' },
      { id: 'warehouse-orders', label: 'Orders', path: '/warehouse/orders' },
      { id: 'warehouse-order-config', label: 'Order Visibility', path: '/warehouse/order-config' },
      { id: 'warehouse-project-warehouse', label: 'Project Warehouse', path: '/warehouse/project-warehouse' },
    ],
  },

  // ========================================
  // 9. CARS & EQUIPMENT - subskrypcja ID: 9
  // ========================================
  {
    id: 'cars',
    label: 'Cars & Equipment',
    icon: 'Truck',
    path: '/cars/management',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.CARS_EQUIPMENT],
    roles: ['admin'],
    submenu: [
      { id: 'cars-management', label: 'Management', path: '/cars/management' },
      { id: 'cars-checklists', label: 'Checklists', path: '/cars/checklists' },
    ],
  },

  // ========================================
  // 10. PROJECTS - widoczny dla PPE, Warehouse, Timesheets, Planning Board
  // ========================================
  {
    id: 'planning',
    label: 'Projects',
    icon: 'Calendar',
    path: '/planning/projects',
    requiredSubscriptions: [
      SUBSCRIPTION_TYPES.PPE,
      SUBSCRIPTION_TYPES.WAREHOUSE,
      SUBSCRIPTION_TYPES.TIMESHEETS,
      SUBSCRIPTION_TYPES.PLANNING_BOARD,
    ],
    roles: ['admin', 'adminves'],
  },

  // ========================================
  // 11. TIMESHEETS - subskrypcja ID: 6
  // ========================================
  {
    id: 'timesheets',
    label: 'Timesheets',
    icon: 'Clock',
    path: '/timesheets/management',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TIMESHEETS],
    roles: ['admin', 'adminves'],
    submenu: [
      { id: 'timesheets-management', label: 'Timesheets Management', path: '/timesheets/management' },
      { id: 'timesheets-submit', label: 'Send Timesheet', path: '/timesheets/submit' },
      { id: 'timesheets-my', label: 'My Timesheets', path: '/timesheets/my' },
      { id: 'timesheets-cpo', label: 'Project Order Management', path: '/timesheets/service-work-order', requiredSubscriptions: [SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER] },
      { id: 'expense-reimbursements', label: 'Daily Spending Refund', path: '/expense-reimbursements' },
    ],
  },

  // ========================================
  // 12. DAILY TASK COORDINATION - subskrypcja PLANNING_BOARD (ID: 8)
  // ========================================
  {
    id: 'daily-task-coordination',
    label: 'Daily Task Coordination',
    icon: 'Calendar',
    path: '/daily-task-coordination',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.PLANNING_BOARD],
    roles: ['admin', 'adminves'],
    allowCoordinator: true,
    submenu: [
      { id: 'dtc-resource-board', label: 'Resource Board', path: '/daily-task-coordination/resource-board' },
      { id: 'dtc-accommodation', label: 'Accommodation', path: '/daily-task-coordination/accommodation' },
      { id: 'dtc-daily-planning', label: 'Project Transport', path: '/daily-task-coordination/daily-planning' },
      { id: 'dtc-toolbox-talks', label: 'Toolbox Talks', path: '/daily-task-coordination/toolbox-talks' },
      { id: 'dtc-travel', label: 'Travel Coordination', path: '/daily-task-coordination/travel-coordination' },
      { id: 'dtc-work-planning', label: 'Daily Work Planning', path: '/daily-task-coordination/work-planning' },
      { id: 'dtc-daily-report', label: 'Daily Report', path: '/daily-task-coordination/daily-report' },
    ],
  },

  // ========================================
  // 13. HSE - subskrypcja ID: 7
  // ========================================
  {
    id: 'hse-documents',
    label: 'HSE Management',
    icon: 'Shield',
    path: '/risk-assessments',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.HSE],
    roles: ['admin', 'adminves'],
    submenu: [
      { id: 'hse-overview', label: 'HSE Overview', path: '/hse' },
      { id: 'risk-assessments', label: 'Risk Assessments', path: '/risk-assessments' },
      { id: 'incidents', label: 'Incidents', path: '/incidents' },
      { id: 'safety-alerts', label: 'Safety Alerts', path: '/safety-alerts' },
      { id: 'actions', label: 'Actions', path: '/actions' },
      {
        id: 'inspections',
        label: 'Inspections & Audits',
        path: '/inspections',
        permissionIds: ['inspections', 'compliance'],
      },
    ],
  },

  // ========================================
  // 14. OEM PROJECTS - widoczne gdy firma jest przypisana do projektu OEM
  // ========================================
  {
    id: 'oem-projects',
    label: 'OEM Projects',
    icon: 'FolderKanban',
    path: '/oem-company',
    requiredSubscriptions: [], // Widoczność kontrolowana przez requiresOEMProject
    roles: ['admin', 'adminves'],
    requiresOEMProject: true, // Specjalny warunek - firma musi być w projekcie OEM
    submenu: [
      { id: 'oem-company-dashboard', label: 'Dashboard', path: '/oem-company' },
      { id: 'oem-company-projects', label: 'Projects', path: '/oem-company/projects' },
      { id: 'oem-company-nominations', label: 'Nominations', path: '/oem-company/nominations' },
      { id: 'oem-company-workpermits', label: 'Work Permits', path: '/oem-company/work-permits' },
    ],
  },

  // ========================================
  // 15. SURVEYS - zawsze dostępne (bez subskrypcji)
  // ========================================
  {
    id: 'surveys',
    label: 'Surveys',
    icon: 'ClipboardList',
    path: '/surveys',
    requiredSubscriptions: [], // Zawsze dostępne
    roles: ['admin', 'adminves'],
  },

  // ========================================
  // 15. PUSH NOTIFICATIONS - zawsze dostępne dla admina
  // ========================================
  {
    id: 'push-notifications',
    label: 'Push Notifications',
    icon: 'Bell',
    path: '/push-notifications',
    requiredSubscriptions: [], // Zawsze dostępne
    roles: ['admin', 'adminves'],
  },
];

// ============================================================================
// MENU DLA USERA (UserDashboard Sidebar)
// ============================================================================

// DEPRECATED - używaj UNIFIED_MENU zamiast USER_MENU
// Ten export jest zachowany dla kompatybilności wstecznej (UserDashboard.js)
export const USER_MENU = [
  // ========================================
  // Zawsze widoczne dla usera (bez subskrypcji)
  // ========================================
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    view: 'dashboard',
    requiredSubscriptions: [], // Zawsze widoczne - treść będzie się zmieniać w zależności od subskrypcji
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: 'FileText',
    view: 'documents',
    requiredSubscriptions: [], // Zawsze widoczne
  },
  {
    id: 'ppe',
    label: 'Your PPE',
    icon: 'HardHat',
    view: 'ppe',
    requiredSubscriptions: [], // Zawsze widoczne dla usera
  },

  // ========================================
  // EQUIPMENT - widoczne tylko dla inspektorów (inspectorOnly)
  // Daje inspektorom dostęp do pełnego widoku Equipment jak admin
  // ========================================
  {
    id: 'equipment-inspector',
    label: 'Equipment',
    icon: 'HardHat',
    path: '/equipment',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.PPE],
    inspectorOnly: true,
    submenu: [
      { id: 'equipment-overview', label: 'Overview', path: '/equipment' },
      { id: 'equipment-management', label: 'Equipment Management', path: '/equipment/management' },
      { id: 'equipment-logbook', label: 'Equipment Log Book', path: '/equipment/logbook' },
      { id: 'equipment-defect-reports', label: 'Defect Reports', path: '/equipment/defect-reports' },
    ],
  },
  {
    id: 'planning-work',
    label: 'Planning Work',
    icon: 'CalendarCheck',
    view: 'planning-work',
    requiredSubscriptions: [], // Zawsze widoczne dla usera
  },
  {
    id: 'surveys',
    label: 'Surveys',
    icon: 'ClipboardList',
    view: 'surveys',
    requiredSubscriptions: [], // Zawsze widoczne dla usera
  },

  // ========================================
  // 1. NOMINATIONS - subskrypcja ID: 1
  // ========================================
  {
    id: 'nominations',
    label: 'Nominations',
    icon: 'Award',
    view: 'my-nominations',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.NOMINATIONS],
  },

  // ========================================
  // 2. WORK PERMITS - subskrypcja ID: 2
  // ========================================
  {
    id: 'work-permit',
    label: 'Work Permit',
    icon: 'Send',
    view: 'request-list',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WORK_PERMITS],
    submenu: [
      { id: 'order-list', label: 'Orders List', view: 'order-list', vestas_only: true },
      { id: 'request-list', label: 'Work Permits List', view: 'request-list' },
      { id: 'request-work', label: 'Create Work Permit', view: 'request-work' },
    ],
  },

  // ========================================
  // 5. WAREHOUSE - subskrypcja ID: 5
  // ========================================
  {
    id: 'my-assignments',
    label: 'My Assignments',
    icon: 'Package',
    view: 'my-assignments',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WAREHOUSE],
  },

  // ========================================
  // 6. TIMESHEETS - subskrypcja ID: 6
  // ========================================
  {
    id: 'timesheets',
    label: 'Timesheets',
    icon: 'Clock',
    view: 'omega-timesheet-submit',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TIMESHEETS],
    submenu: [
      { id: 'timesheet-submit', label: 'Send Timesheet', view: 'omega-timesheet-submit' },
      { id: 'timesheet-my', label: 'My Timesheets', view: 'omega-timesheet-my' },
      { id: 'timesheet-cpo', label: 'Project Order Management', view: 'user-service-work-order', requiredSubscriptions: [SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER] },
    ],
  },
  {
    id: 'timesheet-management',
    label: 'Timesheet Management',
    icon: 'Settings',
    view: 'timesheet-management',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TIMESHEETS],
    coordinatorOnly: true,
    submenu: [
      { id: 'ts-management', label: 'Timesheet Management', view: 'timesheet-management' },
    ],
  },

  // ========================================
  // 8. PLANNING BOARD - subskrypcja ID: 8
  // Coordinator only
  // ========================================
  {
    id: 'daily-task-coordination',
    label: 'Daily Task Coordination',
    icon: 'Calendar',
    view: 'daily-task-coordination',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.PLANNING_BOARD],
    coordinatorOnly: true,
    submenu: [
      { id: 'dtc-accommodation', label: 'Accommodation', path: '/daily-task-coordination/accommodation' },
      { id: 'dtc-daily-planning', label: 'Project Transport', path: '/daily-task-coordination/daily-planning' },
      { id: 'dtc-toolbox-talks', label: 'Toolbox Talks', view: 'toolbox-talks' },
    ],
  },
];

// ============================================================================
// MENU DLA OEM MASTER (OEM Master Dashboard Sidebar)
// OEM Master zarządza ekosystemem projektów OEM
// ============================================================================

export const OEM_MASTER_MENU = [
  // ========================================
  // Dashboard - zawsze widoczny
  // ========================================
  {
    id: 'oem-dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/oem',
    requiredSubscriptions: [],
    roles: ['oem_master'],
  },

  // ========================================
  // Projects - zarządzanie projektami OEM
  // ========================================
  {
    id: 'oem-projects',
    label: 'Projects',
    icon: 'FolderKanban',
    path: '/oem/projects',
    requiredSubscriptions: [],
    roles: ['oem_master'],
    submenu: [
      { id: 'oem-projects-list', label: 'All Projects', path: '/oem/projects' },
      { id: 'oem-projects-create', label: 'Create Project', path: '/oem/projects/create' },
    ],
  },

  // ========================================
  // Companies - firmy w ekosystemie OEM
  // ========================================
  {
    id: 'oem-companies',
    label: 'Companies',
    icon: 'Building2',
    path: '/oem/companies',
    requiredSubscriptions: [],
    roles: ['oem_master'],
  },

  // ========================================
  // Users - użytkownicy tworzeni przez OEM
  // ========================================
  {
    id: 'oem-users',
    label: 'Users',
    icon: 'Users',
    path: '/oem/users',
    requiredSubscriptions: [],
    roles: ['oem_master'],
  },

  // ========================================
  // Authorization Levels - konfiguracja poziomów autoryzacji
  // ========================================
  {
    id: 'oem-authorization-levels',
    label: 'Authorization Levels',
    icon: 'ShieldCheck',
    path: '/oem/authorization-levels',
    requiredSubscriptions: [],
    roles: ['oem_master'],
  },

  // ========================================
  // Nominations - wszystkie nominacje OEM
  // ========================================
  {
    id: 'oem-nominations',
    label: 'Nominations',
    icon: 'Award',
    path: '/oem/nominations',
    requiredSubscriptions: [],
    roles: ['oem_master'],
    submenu: [
      { id: 'oem-nominations-pending', label: 'Pending', path: '/oem/nominations?status=pending' },
      { id: 'oem-nominations-all', label: 'All Nominations', path: '/oem/nominations' },
    ],
  },

  // ========================================
  // Certificates - Global Outlook certyfikatów
  // ========================================
  {
    id: 'oem-certificates',
    label: 'Certificates',
    icon: 'FileCheck',
    path: '/oem/certificates',
    requiredSubscriptions: [],
    roles: ['oem_master'],
  },

  // ========================================
  // Toolbox Master Look - wszystkie toolboxy
  // ========================================
  {
    id: 'oem-toolboxes',
    label: 'Toolbox Talks',
    icon: 'MessageSquare',
    path: '/oem/toolboxes',
    requiredSubscriptions: [],
    roles: ['oem_master'],
  },
];

// ============================================================================
// UNIFIED MENU - Jeden sidebar dla wszystkich ról
// Widoczność elementów zależy od: roles, requiredSubscriptions, allowCoordinator, allowInspector
// ============================================================================

export const UNIFIED_MENU = [
  // ========================================
  // 1. DASHBOARD - widoczny dla wszystkich
  // ========================================
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/dashboard',
    requiredSubscriptions: [],
    roles: ['admin', 'adminves', 'user', 'userves', 'superuser'],
  },

  // ========================================
  // 2. MY WORK PLANNING - planowanie pracy użytkownika
  // ========================================
  {
    id: 'my-planning',
    label: 'My Work Planning',
    icon: 'CalendarCheck',
    path: '/my-planning',
    requiredSubscriptions: [],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
  },

  // ========================================
  // 3. MY DOCUMENTS - dokumenty użytkownika (certyfikaty)
  // ========================================
  {
    id: 'my-documents',
    label: 'My Documents',
    icon: 'FileText',
    path: '/my-documents',
    requiredSubscriptions: [],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
  },

  // ========================================
  // 3b. MY COMPANY ROLES - role użytkownika w firmie
  // ========================================
  {
    id: 'my-company-roles',
    label: 'My Company Roles',
    icon: 'Award',
    path: '/my-company-roles',
    requiredSubscriptions: [],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
    requireCompany: true,
    requireTrainingRoles: true,
  },

  // ========================================
  // 4. MY PPE - osobiste PPE użytkownika
  // ========================================
  {
    id: 'my-ppe',
    label: 'My PPE',
    icon: 'HardHat',
    path: '/my-ppe',
    requiredSubscriptions: [],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
  },

  // ========================================
  // 5. MY ASSIGNMENTS - przydziały z magazynu
  // ========================================
  {
    id: 'my-assignments',
    label: 'My Assignments',
    icon: 'Package',
    path: '/my-assignments',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WAREHOUSE],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
  },

  // ========================================
  // 6. MY NOMINATIONS - nominacje użytkownika
  // ========================================
  {
    id: 'my-nominations',
    label: 'My Nominations',
    icon: 'Award',
    path: '/my-nominations',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.NOMINATIONS],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
  },

  // ========================================
  // 7. MY WORK PERMITS - work permits użytkownika
  // ========================================
  {
    id: 'my-workpermits',
    label: 'My Work Permits',
    icon: 'Send',
    path: '/my-workpermits',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WORK_PERMITS],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
    submenu: [
      { id: 'my-workpermits-list', label: 'My Work Permits', path: '/my-workpermits' },
      { id: 'my-workpermits-create', label: 'Create Work Permit', path: '/my-workpermits/create' },
      { id: 'my-workpermits-orders', label: 'Orders List', path: '/my-workpermits/orders', vestasOnly: true },
    ],
  },

  // ========================================
  // 8. MY TIMESHEETS - timesheety użytkownika
  // ========================================
  {
    id: 'my-timesheets',
    label: 'My Timesheets',
    icon: 'Clock',
    path: '/my-timesheets',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TIMESHEETS],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
    submenu: [
      { id: 'my-timesheets-list', label: 'My Timesheets', path: '/my-timesheets' },
      { id: 'my-timesheets-submit', label: 'Send Timesheet', path: '/my-timesheets/submit' },
      { id: 'my-invoices', label: 'My Draft', path: '/my-invoices', invoiceOnly: true },
      { id: 'my-drafts-v2', label: 'My Drafts', path: '/my-drafts', tsEnableDraftsOnly: true },
      { id: 'my-cpo', label: 'My Project Orders', path: '/my-cpo', requiredSubscriptions: [SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER] },
    ],
  },

  // ========================================
  // 9. MY TOOLBOX TALKS
  // ========================================
  {
    id: 'my-toolbox-talks',
    label: 'My Toolbox Talks',
    icon: 'HardHat',
    path: '/my-toolbox-talks',
    requiredSubscriptions: [],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
  },

  // ========================================
  // 10. SURVEYS - ankiety (user/inspector/coordinator)
  // Wersja adminowa jest niżej, po OEM Projects
  // ========================================
  {
    id: 'my-surveys',
    label: 'Surveys',
    icon: 'ClipboardList',
    path: '/surveys',
    requiredSubscriptions: [],
    roles: ['user', 'userves', 'admin', 'adminves', 'superuser'],
  },

  // ========================================
  // SEPARATOR - widoczny tylko dla inspectorów i coordinatorów
  // Oddziela sekcję "my" od dodatkowych uprawnień inspector/coordinator
  // Admini nie potrzebują separatora - w trybie admin widzą tylko pozycje adminowe
  // ========================================
  {
    id: 'separator-admin',
    type: 'separator',
    label: 'Administration',
    roles: ['_inspector_or_coordinator_only'],
    allowInspector: true,
    allowCoordinator: true,
  },

  // ========================================
  // USERS - zarządzanie użytkownikami (admin)
  // ========================================
  {
    id: 'users',
    label: 'Users',
    icon: 'Users',
    path: '/users',
    requiredSubscriptions: [
      SUBSCRIPTION_TYPES.NOMINATIONS,
      SUBSCRIPTION_TYPES.WORK_PERMITS,
      SUBSCRIPTION_TYPES.TRAININGS,
      SUBSCRIPTION_TYPES.PPE,
      SUBSCRIPTION_TYPES.WAREHOUSE,
      SUBSCRIPTION_TYPES.TIMESHEETS,
      SUBSCRIPTION_TYPES.PLANNING_BOARD,
    ],
    roles: ['admin', 'adminves', 'superuser'],
    submenu: [
      { id: 'users-manage', label: 'Manage Users', path: '/users' },
      { id: 'users-roles', label: 'Roles', path: '/users/roles' },
      { id: 'users-archives', label: 'Archives', path: '/users/archives' },
      { id: 'users-contracts', label: 'Contracts & Rates', path: '/users/contracts' },
    ],
  },

  // ========================================
  // CERTIFICATES - certyfikaty (admin)
  // ========================================
  {
    id: 'certificateexp',
    label: 'Certificates',
    icon: 'AlarmClock',
    path: '/certificateexp',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TRAININGS],
    roles: ['admin', 'adminves', 'superuser'],
    submenu: [
      { id: 'certificates-overview', label: 'Overview', path: '/certificateexp' },
      { id: 'documents-to-verify', label: 'Documents to Verify', path: '/documents-to-verify' },
    ],
  },

  // ========================================
  // TRAININGS - szkolenia (admin)
  // ========================================
  {
    id: 'trainings',
    label: 'Trainings',
    icon: 'GraduationCap',
    path: '/trainings',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TRAININGS],
    roles: ['admin', 'adminves', 'superuser'],
    submenu: [
      { id: 'trainings-users', label: 'User Panel', path: '/trainings/users' },
      { id: 'trainings-panel', label: 'Training Panel', path: '/trainings/panel' },
      { id: 'trainings-management', label: 'Management', path: '/trainings/management' },
      { id: 'trainings-analysis', label: 'Cost Analysis', path: '/trainings/analysis' },
      { id: 'trainings-emails', label: 'Email Templates', path: '/trainings/emails' },
    ],
  },

  // ========================================
  // NOMINATIONS - zarządzanie nominacjami (admin)
  // ========================================
  {
    id: 'nominations',
    label: 'Nominations',
    icon: 'Award',
    path: '/nominations/list',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.NOMINATIONS],
    roles: ['admin', 'adminves', 'superuser'],
    submenu: [
      { id: 'nominations-create', label: 'Create', path: '/nominations/create' },
      { id: 'nominations-list', label: 'Nominations List', path: '/nominations/list' },
    ],
  },

  // ========================================
  // WORK PERMITS - zarządzanie work permits (admin)
  // ========================================
  {
    id: 'workpermits',
    label: 'Work Permits',
    icon: 'Send',
    path: '/workpermits',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WORK_PERMITS],
    roles: ['admin'],
    submenu: [
      { id: 'workpermits-list', label: 'All Permits', path: '/workpermits' },
      { id: 'workpermits-teams', label: 'Teams', path: '/teammanager' },
    ],
  },
  {
    id: 'workpermit-vestas',
    label: 'Work Orders',
    icon: 'Send',
    path: '/workpermit/admin',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WORK_PERMITS],
    roles: ['adminves'],
  },

  // ========================================
  // EQUIPMENT - zarządzanie sprzętem (admin + inspector)
  // ========================================
  {
    id: 'equipment',
    label: 'Equipment',
    icon: 'HardHat',
    path: '/equipment',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.PPE],
    roles: ['admin'],
    allowInspector: true,
    submenu: [
      { id: 'equipment-overview', label: 'Overview', path: '/equipment' },
      { id: 'equipment-management', label: 'Equipment Management', path: '/equipment/management' },
      { id: 'equipment-logbook', label: 'Equipment Log Book', path: '/equipment/logbook' },
      { id: 'equipment-defect-reports', label: 'Defect Reports', path: '/equipment/defect-reports' },
    ],
  },

  // =====================================================
  // Equipment via cooperation (no own PPE sub)
  // Widoczne tylko gdy firma ma ppe_via_cooperation
  // i NIE ma własnej subskrypcji PPE
  // =====================================================
  {
    id: 'equipment-cooperation',
    label: 'Equipment',
    icon: 'HardHat',
    path: '/equipment',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.PPE_VIA_COOPERATION],
    hideIfHasPpe: true,
    permissionId: 'equipment',
    roles: ['admin'],
    submenu: [
      { id: 'equipment-management', label: 'Equipment Management', path: '/equipment/management' },
    ],
  },

  // ========================================
  // WAREHOUSE - magazyn (admin)
  // ========================================
  {
    id: 'warehouse',
    label: 'Warehouse',
    icon: 'Package',
    path: '/warehouse/inventory',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.WAREHOUSE],
    roles: ['admin', 'superuser'],
    submenu: [
      { id: 'warehouse-inventory', label: 'Inventory', path: '/warehouse/inventory' },
      { id: 'warehouse-history', label: 'Allocations', path: '/warehouse/history' },
      { id: 'warehouse-settings', label: 'Settings', path: '/warehouse/settings' },
      { id: 'warehouse-project-orders', label: 'Project Orders', path: '/warehouse/project-orders' },
      { id: 'warehouse-orders', label: 'Orders', path: '/warehouse/orders' },
      { id: 'warehouse-order-config', label: 'Order Visibility', path: '/warehouse/order-config' },
      { id: 'warehouse-project-warehouse', label: 'Project Warehouse', path: '/warehouse/project-warehouse' },
    ],
  },

  // ========================================
  // CARS & EQUIPMENT - samochody (admin)
  // ========================================
  {
    id: 'cars',
    label: 'Cars & Equipment',
    icon: 'Truck',
    path: '/cars/management',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.CARS_EQUIPMENT],
    roles: ['admin', 'superuser'],
    submenu: [
      { id: 'cars-management', label: 'Management', path: '/cars/management' },
      { id: 'cars-checklists', label: 'Checklists', path: '/cars/checklists' },
    ],
  },

  // ========================================
  // PROJECTS - projekty (admin)
  // ========================================
  {
    id: 'planning',
    label: 'Projects',
    icon: 'Calendar',
    path: '/planning/projects',
    requiredSubscriptions: [
      SUBSCRIPTION_TYPES.PPE,
      SUBSCRIPTION_TYPES.WAREHOUSE,
      SUBSCRIPTION_TYPES.TIMESHEETS,
      SUBSCRIPTION_TYPES.PLANNING_BOARD,
    ],
    roles: ['admin', 'adminves', 'superuser'],
  },

  // ========================================
  // TIMESHEETS - zarządzanie timesheets (admin + coordinator)
  // ========================================
  {
    id: 'timesheets',
    label: 'Timesheets',
    icon: 'Clock',
    path: '/timesheets/management',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.TIMESHEETS],
    roles: ['admin', 'adminves', 'superuser'],
    allowCoordinator: true,
    submenu: [
      {
        id: 'timesheets-management',
        label: 'Timesheets Management',
        path: '/timesheets/management',
      },
      {
        id: 'timesheets-invoices',
        label: 'Draft',
        path: '/timesheets/invoices',
        invoiceOnly: true,
      },
      {
        id: 'timesheets-drafts-v2',
        label: 'Drafts',
        path: '/timesheets/drafts',
        tsEnableDraftsOnly: true,
      },
      {
        id: 'timesheets-cpo',
        label: 'Project Order Management',
        path: '/timesheets/service-work-order',
        requiredSubscriptions: [SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER],
      },
      {
        id: 'expense-reimbursements',
        label: 'Daily Spending Refund',
        path: '/expense-reimbursements',
      },
    ],
  },

  // ========================================
  // DAILY TASK COORDINATION (admin + coordinator)
  // ========================================
  {
    id: 'daily-task-coordination',
    label: 'Daily Task Coordination',
    icon: 'Calendar',
    path: '/daily-task-coordination',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.PLANNING_BOARD],
    roles: ['admin', 'adminves', 'superuser'],
    allowCoordinator: true,
    submenu: [
      { id: 'dtc-resource-board', label: 'Resource Board', path: '/daily-task-coordination/resource-board', roles: ['admin', 'adminves', 'superuser'] },
      { id: 'dtc-daily-planning', label: 'Project Transport', path: '/daily-task-coordination/daily-planning' },
      { id: 'dtc-accommodation', label: 'Accommodation', path: '/daily-task-coordination/accommodation' },
      { id: 'dtc-toolbox-talks', label: 'Toolbox Talks', path: '/daily-task-coordination/toolbox-talks' },
      { id: 'dtc-travel', label: 'Travel Coordination', path: '/daily-task-coordination/travel-coordination', roles: ['admin', 'adminves', 'superuser'] },
      { id: 'dtc-work-planning', label: 'Daily Work Planning', path: '/daily-task-coordination/work-planning', roles: ['admin', 'adminves', 'superuser'], allowCoordinator: true },
      { id: 'dtc-daily-report', label: 'Daily Report', path: '/daily-task-coordination/daily-report' },
    ],
  },

  // ========================================
  // HSE DOCUMENTS (admin)
  // ========================================
  {
    id: 'hse-documents',
    label: 'HSE Management',
    icon: 'Shield',
    path: '/risk-assessments',
    requiredSubscriptions: [SUBSCRIPTION_TYPES.HSE],
    roles: ['admin', 'adminves', 'superuser'],
    submenu: [
      { id: 'hse-overview', label: 'HSE Overview', path: '/hse' },
      { id: 'risk-assessments', label: 'Risk Assessments', path: '/risk-assessments' },
      { id: 'incidents', label: 'Incidents', path: '/incidents' },
      { id: 'safety-alerts', label: 'Safety Alerts', path: '/safety-alerts' },
      { id: 'actions', label: 'Actions', path: '/actions' },
      {
        id: 'inspections',
        label: 'Inspections & Audits',
        path: '/inspections',
        permissionIds: ['inspections', 'compliance'],
      },
    ],
  },

  // ========================================
  // OEM PROJECTS - dla firm w projektach OEM
  // ========================================
  {
    id: 'oem-projects',
    label: 'OEM Projects',
    icon: 'FolderKanban',
    path: '/oem-company',
    requiredSubscriptions: [],
    roles: ['admin', 'adminves'],
    requiresOEMProject: true,
    submenu: [
      { id: 'oem-company-dashboard', label: 'Dashboard', path: '/oem-company' },
      { id: 'oem-company-projects', label: 'Projects', path: '/oem-company/projects' },
      { id: 'oem-company-nominations', label: 'Nominations', path: '/oem-company/nominations' },
      { id: 'oem-company-workpermits', label: 'Work Permits', path: '/oem-company/work-permits' },
    ],
  },

  // ========================================
  // SURVEYS - ankiety (admin)
  // ========================================
  {
    id: 'surveys',
    label: 'Surveys',
    icon: 'ClipboardList',
    path: '/surveys',
    requiredSubscriptions: [],
    roles: ['admin', 'adminves', 'superuser'],
  },

  // ========================================
  // PUSH NOTIFICATIONS (admin)
  // ========================================
  {
    id: 'push-notifications',
    label: 'Push Notifications',
    icon: 'Bell',
    path: '/push-notifications',
    requiredSubscriptions: [],
    roles: ['admin', 'adminves', 'superuser'],
  },
];

// Set of menu item IDs that belong to the user section (before separator-admin).
// Used by hasAccessToMenuItem to skip menuPermissions check for user items —
// menuPermissions only restrict admin features, not personal user features.
const _USER_ITEM_IDS = new Set();
for (const _item of UNIFIED_MENU) {
  if (_item.id === 'separator-admin') break;
  _USER_ITEM_IDS.add(_item.id);
}

// ============================================================================
// OCHRONA TRAS
// Mapowanie ścieżek do wymaganych subskrypcji (ANY - wystarczy jedna)
// ============================================================================

export const ROUTE_PROTECTION = {
  // Admin routes - Dashboard zawsze dostępny (bez ochrony)
  // '/admin': [], // Dashboard nie wymaga subskrypcji

  // 1. NOMINATIONS
  '/nominations': [SUBSCRIPTION_TYPES.NOMINATIONS],

  // 2. WORK PERMITS
  '/workpermits': [SUBSCRIPTION_TYPES.WORK_PERMITS],
  '/workpermit/admin': [SUBSCRIPTION_TYPES.WORK_PERMITS],
  '/teammanager': [SUBSCRIPTION_TYPES.WORK_PERMITS],

  // 3. TRAININGS
  '/certificateexp': [SUBSCRIPTION_TYPES.TRAININGS],
  '/documents-to-verify': [SUBSCRIPTION_TYPES.TRAININGS],
  '/trainings': [SUBSCRIPTION_TYPES.TRAININGS],

  // 4. PPE
  '/equipment': [SUBSCRIPTION_TYPES.PPE, SUBSCRIPTION_TYPES.PPE_VIA_COOPERATION],

  // 5. WAREHOUSE
  '/warehouse': [SUBSCRIPTION_TYPES.WAREHOUSE],

  // 6. TIMESHEETS
  '/timesheets/service-work-order': [SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER],
  '/timesheets': [SUBSCRIPTION_TYPES.TIMESHEETS],
  '/expense-reimbursements': [SUBSCRIPTION_TYPES.TIMESHEETS],

  // 7. HSE
  '/hse': [SUBSCRIPTION_TYPES.HSE],
  '/risk-assessments': [SUBSCRIPTION_TYPES.HSE],
  '/risk-assessment-templates': [SUBSCRIPTION_TYPES.HSE],
  '/jsa': [SUBSCRIPTION_TYPES.HSE],
  '/incidents': [SUBSCRIPTION_TYPES.HSE],
  '/actions': [SUBSCRIPTION_TYPES.HSE],
  '/compliance': [SUBSCRIPTION_TYPES.HSE],
  '/inspections': [SUBSCRIPTION_TYPES.HSE],

  // 8. PLANNING BOARD
  '/daily-task-coordination': [SUBSCRIPTION_TYPES.PLANNING_BOARD],

  // 9. CARS & EQUIPMENT
  '/cars': [SUBSCRIPTION_TYPES.CARS_EQUIPMENT],
  '/driver': [SUBSCRIPTION_TYPES.CARS_EQUIPMENT],

  // USERS - wymagana jakakolwiek z tych subskrypcji
  '/users': [
    SUBSCRIPTION_TYPES.NOMINATIONS,
    SUBSCRIPTION_TYPES.WORK_PERMITS,
    SUBSCRIPTION_TYPES.TRAININGS,
    SUBSCRIPTION_TYPES.PPE,
    SUBSCRIPTION_TYPES.WAREHOUSE,
    SUBSCRIPTION_TYPES.TIMESHEETS,
    SUBSCRIPTION_TYPES.PLANNING_BOARD,
  ],

  // PROJECTS - PPE, Warehouse, Timesheets lub Planning Board
  '/planning': [
    SUBSCRIPTION_TYPES.PPE,
    SUBSCRIPTION_TYPES.WAREHOUSE,
    SUBSCRIPTION_TYPES.TIMESHEETS,
    SUBSCRIPTION_TYPES.PLANNING_BOARD,
  ],

  // User routes (nowe unified routes)
  '/my-nominations': [SUBSCRIPTION_TYPES.NOMINATIONS],
  '/my-workpermits': [SUBSCRIPTION_TYPES.WORK_PERMITS],
  '/my-assignments': [SUBSCRIPTION_TYPES.WAREHOUSE],
  '/my-timesheets': [SUBSCRIPTION_TYPES.TIMESHEETS],
  '/my-invoices': [SUBSCRIPTION_TYPES.TIMESHEETS],
  '/my-cpo': [SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER],
  // Dashboard, My Documents, My PPE, My Planning - bez wymaganych subskrypcji
};

// ============================================================================
// MAPOWANIE TRAS NA UPRAWNIENIE MENU (rola firmowa)
// Prefiks ścieżki -> id kategorii w menu_permissions.
// Używane przez PrivateRoute, by egzekwować rolę firmową na trasach DOKŁADNIE
// tak, jak hasAccessToMenuItem egzekwuje ją w sidebarze: coordinator/inspector
// z przypisaną rolą firmową ma dostęp tylko do kategorii wskazanych w roli.
// Klucze muszą odpowiadać kategoriom z UNIFIED_MENU (i VALID_MENU_IDS w backendzie).
// ============================================================================

export const ROUTE_MENU_PERMISSION = {
  '/users': 'users',
  '/certificateexp': 'certificateexp',
  '/documents-to-verify': 'certificateexp',
  '/trainings': 'trainings',
  '/nominations': 'nominations',
  '/workpermits': 'workpermits',
  '/teammanager': 'workpermits',
  '/workpermit/admin': 'workpermits',
  '/equipment': 'equipment',
  '/warehouse': 'warehouse',
  '/cars': 'cars',
  '/driver': 'cars',
  '/planning': 'planning',
  '/timesheets': 'timesheets',
  '/expense-reimbursements': 'timesheets',
  '/daily-task-coordination': 'daily-task-coordination',
  '/hse': 'hse-documents',
  '/risk-assessments': 'hse-documents',
  '/risk-assessment-templates': 'hse-documents',
  '/jsa': 'hse-documents',
  '/incidents': 'hse-documents',
  '/safety-alerts': 'hse-documents',
  '/actions': 'hse-documents',
  '/compliance': 'hse-documents',
  '/inspections': 'hse-documents',
  '/surveys': 'surveys',
  '/push-notifications': 'push-notifications',
  '/oem-company': 'oem-projects',
};

/**
 * Zwraca klucz uprawnienia menu (id kategorii) dla danej ścieżki, dopasowując
 * NAJDŁUŻSZY pasujący prefiks (granica na '/' lub dokładne dopasowanie).
 * Zwraca null, gdy trasa nie jest powiązana z żadną kategorią administracyjną
 * (wtedy guard nie nakłada ograniczeń roli firmowej).
 * @param {string} pathname
 * @returns {string|null}
 */
export const getRouteMenuPermission = (pathname) => {
  if (!pathname) return null;
  let best = null;
  for (const [prefix, key] of Object.entries(ROUTE_MENU_PERMISSION)) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      if (!best || prefix.length > best.prefix.length) {
        best = { prefix, key };
      }
    }
  }
  return best ? best.key : null;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Sprawdza czy użytkownik ma dostęp do elementu menu
 * @param {Object} menuItem - Element z ADMIN_MENU lub USER_MENU
 * @param {Object} subscriptionChecks - Obiekt z subscription_checks z API
 * @param {Object} user - Obiekt użytkownika z kontekstu
 * @returns {boolean}
 */
export const hasAccessToMenuItem = (menuItem, subscriptionChecks, user) => {
  // Separator "Administration" - widoczny dla inspectorów, coordinatorów i userów z company role
  if (menuItem.id === 'separator-admin') {
    return user?.isCoordinator || user?.inspector || (user?.menuPermissions?.length > 0);
  }

  // Wyklucz pozycje dla coordinatorów (wyświetlamy im wersję adminową zamiast userowej)
  if (menuItem.excludeIfCoordinator && user?.isCoordinator) {
    return false;
  }

  // NAJPIERW sprawdź role (dla menu z określonymi rolami jak OEM_MASTER_MENU)
  if (menuItem.roles && menuItem.roles.length > 0) {
    const hasMenuPermissions = user?.menuPermissions?.length > 0;
    const hasRoleAccess = menuItem.roles.includes(user?.role);
    const hasCoordinatorAccess = menuItem.allowCoordinator && user?.isCoordinator;
    const hasInspectorAccess = menuItem.allowInspector && user?.inspector;
    // Company role - check if menu_permissions include this menu item's ID
    // permissionId allows an item to reuse another item's permission key
    const permissionKeys = menuItem.permissionIds
      || [menuItem.permissionId || menuItem.id];
    const hasCompanyRoleAccess = hasMenuPermissions
      && permissionKeys.some(key => user.menuPermissions.includes(key));

    // menuPermissions (rola firmowa) ograniczają admin items (po separator-admin)
    // dla KAŻDEJ ścieżki dostępu — roli, coordinatora i inspectora.
    // Coordinator/inspector podlegają tym samym zasadom co admin: gdy mają
    // przypisaną rolę firmową, widzą tylko pozycje wskazane w jej menu_permissions
    // (nie wystarczy sama flaga allowCoordinator/allowInspector na pozycji).
    // User items (dashboard, my-documents itp.) nie podlegają menuPermissions.
    const isUserItem = _USER_ITEM_IDS.has(menuItem.id);
    const hasAnyAccess = hasRoleAccess || hasCoordinatorAccess || hasInspectorAccess;
    if (hasMenuPermissions && !isUserItem && hasAnyAccess) {
      if (!hasCompanyRoleAccess) {
        return false;
      }
    } else if (!hasRoleAccess && !hasCoordinatorAccess && !hasInspectorAccess && !hasCompanyRoleAccess) {
      return false;
    }
  }

  // Sprawdź czy coordinator only (dla USER_MENU)
  if (menuItem.coordinatorOnly && !user?.isCoordinator) {
    return false;
  }

  // Sprawdź czy inspector only (dla USER_MENU - widoczne tylko dla inspektorów)
  if (menuItem.inspectorOnly && !user?.inspector) {
    return false;
  }

  // Ukryj pozycję cooperation-equipment gdy firma ma własną subskrypcję PPE
  if (menuItem.hideIfHasPpe && subscriptionChecks?.ppe === true) {
    return false;
  }

  // Sprawdź czy wymaga uczestnictwa w projekcie OEM
  if (menuItem.requiresOEMProject) {
    // subscriptionChecks zawiera has_oem_projects z API
    if (!subscriptionChecks?.has_oem_projects) {
      return false;
    }
  }

  // Jeśli brak wymaganych subskrypcji - widoczne (po sprawdzeniu ról)
  if (!menuItem.requiredSubscriptions || menuItem.requiredSubscriptions.length === 0) {
    return true;
  }

  // Sprawdź subskrypcje (ANY - wystarczy jedna)
  return menuItem.requiredSubscriptions.some(subType => {
    const key = SUBSCRIPTION_KEYS[subType];
    return subscriptionChecks?.[key] === true;
  });
};

/**
 * Sprawdza czy trasa jest chroniona i czy użytkownik ma dostęp
 * @param {string} pathname - Aktualna ścieżka
 * @param {Object} subscriptionChecks - Obiekt z subscription_checks z API
 * @returns {boolean}
 */
export const hasAccessToRoute = (pathname, subscriptionChecks) => {
  // Znajdź matching route
  const matchingRoute = Object.entries(ROUTE_PROTECTION).find(([route]) =>
    pathname.startsWith(route)
  );

  // Jeśli trasa nie jest chroniona - dostęp
  if (!matchingRoute) {
    return true;
  }

  const [, requiredSubs] = matchingRoute;

  // Sprawdź czy użytkownik ma KTÓRĄKOLWIEK z wymaganych subskrypcji
  return requiredSubs.some(subType => {
    const key = SUBSCRIPTION_KEYS[subType];
    return subscriptionChecks?.[key] === true;
  });
};

/**
 * Filtruje menu na podstawie subskrypcji i roli użytkownika
 * @param {Array} menu - ADMIN_MENU lub USER_MENU
 * @param {Object} subscriptionChecks - Obiekt z subscription_checks z API
 * @param {Object} user - Obiekt użytkownika
 * @returns {Array} - Przefiltrowane menu
 */
export const filterMenuBySubscriptions = (menu, subscriptionChecks, user) => {
  return menu.filter(item => hasAccessToMenuItem(item, subscriptionChecks, user));
};

/**
 * Pobiera nazwę subskrypcji po ID
 * @param {number} typeId - ID typu subskrypcji
 * @returns {string}
 */
export const getSubscriptionName = (typeId) => {
  return SUBSCRIPTION_NAMES[typeId] || `Unknown (${typeId})`;
};

/**
 * Pobiera klucz API dla typu subskrypcji
 * @param {number} typeId - ID typu subskrypcji
 * @returns {string}
 */
export const getSubscriptionKey = (typeId) => {
  return SUBSCRIPTION_KEYS[typeId] || null;
};
